import fmt_date, { format_datetime_dd_mmm_yy as fmt_datetime } from '../../utils/format_date_dd_mmm_yy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, Collapse, Input, Modal, ModalBody, Nav, NavItem, NavLink } from 'reactstrap';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import EvidenceModal from '../StoreDashboard/ClientHealthTab/EvidenceModal';
import HistoryTimeline from '../StoreDashboard/ClientHealthTab/HistoryTimeline';
import ChatDrawer from '../StoreDashboard/ClientHealthTab/ChatDrawer';
import { FindingDetailCard } from '../StoreDashboard/ClientHealthTab/Findings';
import { SortToggle } from '../StoreDashboard/ClientHealthTab/task_ui';
import { TaskCard } from '../StoreDashboard/ClientHealthTab/Actionables';
import MeetingCommitments from './MeetingCommitments';
import { show_meetings_card } from './meeting_commitments_helpers';

/**
 * Rep-centric portfolio view, parameterized by `kind`:
 *   - kind='findings'    → My Findings page (Findings + their proposed solutions)
 *   - kind='actionables' → My Actionables page (committed work)
 *   - kind undefined     → legacy combined view (kept for back-compat)
 *
 * Default grouping is by client because reps think in terms of "what's
 * happening with X account" first; toggle to "by item" for cross-client scan
 * mode (e.g. "show me every HIGH severity across my book").
 *
 * Inline status changes + rep-note edits go to the same PATCH endpoints
 * already used by ClientHealthTab. Evidence references open the shared
 * EvidenceModal.
 */

const SEVERITY_COLOR = {
    CRITICAL: '#c0392b',
    HIGH: '#e67e22',
    MEDIUM: '#f1c40f',
    LOW: '#3498db',
    INFO: '#95a5a6',
};
const SEVERITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };

const MyWork = ({ kind }) => {
    const [data, set_data] = useState(null);
    const [err, set_err] = useState(null);
    const [loading, set_loading] = useState(false);
    // busy_id state removed along with the inline mutation helpers
    // (status changes now flow through the per-client chat).
    // Itemwise list is the default — reps scan a flat, severity-sorted list
    // across clients; "by-client" is the secondary roll-up view they switch
    // to when they want per-org grouping.
    const [view_mode, set_view_mode] = useState('by-item'); // 'by-item' | 'by-client'
    // When kind is set, lock the item filter; otherwise legacy 'all' default.
    const [item_filter, set_item_filter] = useState(kind || 'all'); // 'all' | 'findings' | 'actionables'
    const [severity_filter, set_severity_filter] = useState('all'); // 'all' | 'HIGH+' | severity-name
    const [sort_by, set_sort_by] = useState('severity'); // 'severity' (default) | 'newest' (created_at desc)
    // CS-rep filter — only meaningful in Team/All scope (My scope is one rep).
    // Value is a CsExecutive id, or 'all'. Reset to 'all' when scope changes.
    const [rep_filter, set_rep_filter] = useState('all');
    // Active (OPEN / in-progress) vs Archived (resolved / dismissed / done / deferred).
    // Backend /me/work defaults to active; archived passes the closed status sets.
    const [archived, set_archived] = useState(false);
    const [evidence_open, set_evidence_open] = useState({ ev: null, storeId: null });
    const [history_open, set_history_open] = useState({ kind: null, item: null, storeId: null });
    const [chat_open, set_chat_open] = useState(false);
    // Per-finding modal: clicking a finding row pops the same FindingDetailCard
    // (with chromeless=true) inside a Modal. Portfolio chat closes when modal
    // opens so the two surfaces don't overlap on screen.
    const [finding_modal, set_finding_modal] = useState({ finding: null, storeId: null });
    const open_finding_modal = useCallback((finding, storeId) => {
        set_chat_open(false);
        set_finding_modal({ finding, storeId });
    }, []);
    const close_finding_modal = useCallback(() => {
        set_finding_modal({ finding: null, storeId: null });
    }, []);
    // 3-tab pattern mirrored from Tickets: My / Team / All. Backend supports
    // ?scope=my|team|all; the 'all' tab is admin-only on the server side.
    const [scope, set_scope] = useState('my');

    const [impersonated_rep_email, set_impersonated_rep_email] = useState(null);

    // Rep roster + caller's role/self — drives the shared TaskCard's owner
    // rep-select and its edit-rights gating (owner / CS lead / admin only).
    // Role-scoped by the backend; independent of the portfolio scope, so
    // fetched once.
    const [reps, set_reps] = useState([]);
    const [rep_role, set_rep_role] = useState('rep'); // 'rep' | 'lead' | 'admin'
    const [rep_self, set_rep_self] = useState(null);
    const [busy_id, set_busy_id] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await cs_analysis_fetch({ path: '/api/v1/cs/reps' });
                if (cancelled) return;
                set_reps(r && Array.isArray(r.reps) ? r.reps : []);
                set_rep_role(r && r.role ? r.role : 'rep');
                set_rep_self(r && r.self ? r.self : null);
            } catch (_e) { /* non-fatal — cards render read-only if reps unavailable */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        set_loading(true);
        set_err(null);
        try {
            const query = { scope };
            if (impersonated_rep_email) query.rep_email = impersonated_rep_email;
            if (archived) {
                query.findingStatus = 'MITIGATED,RESOLVED,DISMISSED';
                query.actionableStatus = 'DONE,DISMISSED,DEFERRED';
            }
            const resp = await cs_analysis_fetch({
                path: '/api/v1/cs/me/work',
                query,
            });
            set_data(resp);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_loading(false);
        }
    }, [impersonated_rep_email, scope, archived]);

    useEffect(() => {
        load();
    }, [load]);

    // Per-task PATCH for the shared TaskCard. Each card is bound to its own
    // client's storeId at the render site (tasks span clients in this view),
    // so on_patch flows through here → PATCH → full portfolio reload (matches
    // the per-client tab's refresh-after-write behaviour).
    const patch_task = useCallback(async (storeId, id, body) => {
        if (!storeId || busy_id) return;
        set_busy_id(id);
        set_err(null);
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(storeId)}/actionables/${id}`,
                method: 'PATCH',
                body,
            });
            await load();
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy_id(null);
        }
    }, [busy_id, load]);

    // Reset the rep filter when the scope changes — a rep selected under
    // "Team" is meaningless (and hidden) under "My", and would silently
    // filter everything out.
    useEffect(() => {
        set_rep_filter('all');
    }, [scope]);

    // Inline mutation helpers (patch_finding / patch_actionable) were
    // removed along with the row-level Accept/Resolve/Dismiss buttons —
    // status changes + notes now flow through the per-client chat. The
    // portfolio view is read-only; "Open client" link bridges to the
    // chat-based workflow.

    // ---- Derived view state ----

    // Distinct CS reps present in the loaded portfolio (team/all scope). Used
    // to populate the rep-filter dropdown. Derived from each client's csOwner,
    // which the backend includes only on team/all responses.
    const rep_options = useMemo(() => {
        if (!data || !data.clients) return [];
        const by_id = new Map();
        for (const g of data.clients) {
            const o = g.client && g.client.csOwner;
            if (o && o.id && !by_id.has(o.id)) {
                const name = [o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.email || o.id.slice(0, 8);
                by_id.set(o.id, { id: o.id, name });
            }
        }
        return [...by_id.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [data]);

    // ts_of — created_at millis for a finding/actionable, for the 'newest' sort.
    const ts_of = (it) => {
        const t = it && it.createdAt ? new Date(it.createdAt).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
    };
    const sort_items = useCallback((arr, get_item) => {
        // Copy before sorting — filter_findings may hand back the source array
        // reference, and we must not mutate data.clients in place.
        arr = arr.slice();
        arr.sort((x, y) => {
            const ix = get_item(x);
            const iy = get_item(y);
            if (sort_by === 'newest') {
                const d = ts_of(iy) - ts_of(ix); // newest first
                if (d !== 0) return d;
            }
            // severity-first (default, and tie-break for 'newest')
            const sx = ix && ix.severity != null ? (SEVERITY_ORDER[ix.severity] != null ? SEVERITY_ORDER[ix.severity] : 9) : 5;
            const sy = iy && iy.severity != null ? (SEVERITY_ORDER[iy.severity] != null ? SEVERITY_ORDER[iy.severity] : 9) : 5;
            return sx - sy;
        });
        return arr;
    }, [sort_by]);

    const visible_clients = useMemo(() => {
        if (!data || !data.clients) return [];
        return data.clients
            .filter((g) => rep_filter === 'all' || (g.client && g.client.csOwner && g.client.csOwner.id === rep_filter))
            .map((g) => ({
                ...g,
                findings: sort_items(filter_findings(g.findings, item_filter, severity_filter), (f) => f),
                actionables: filter_actionables(g.actionables, item_filter),
            }))
            .filter((g) => g.findings.length > 0 || g.actionables.length > 0);
    }, [data, item_filter, severity_filter, rep_filter, sort_items]);

    const flat_items = useMemo(() => {
        const items = [];
        for (const g of visible_clients) {
            for (const f of g.findings) items.push({ type: 'finding', clientId: g.client.id, storeId: g.client.storeId, clientName: g.client.name, csOwner: g.client.csOwner, item: f });
            for (const a of g.actionables) items.push({ type: 'actionable', clientId: g.client.id, storeId: g.client.storeId, clientName: g.client.name, csOwner: g.client.csOwner, item: a });
        }
        if (sort_by === 'newest') {
            // Newest-first across all items (findings + actionables share createdAt).
            items.sort((x, y) => {
                const d = ts_of(y.item) - ts_of(x.item);
                if (d !== 0) return d;
                return x.clientName.localeCompare(y.clientName);
            });
        } else {
            // Severity-first ordering for cross-client scan mode (default).
            items.sort((x, y) => {
                const sx = x.type === 'finding' ? (SEVERITY_ORDER[x.item.severity] != null ? SEVERITY_ORDER[x.item.severity] : 9) : 5;
                const sy = y.type === 'finding' ? (SEVERITY_ORDER[y.item.severity] != null ? SEVERITY_ORDER[y.item.severity] : 9) : 5;
                if (sx !== sy) return sx - sy;
                return x.clientName.localeCompare(y.clientName);
            });
        }
        return items;
    }, [visible_clients, sort_by]);

    // Derive visible tabs from the backend's tab-gating booleans. Plain
    // reps see My only; Leads see My + Team; admins see all three (minus
    // My if they aren't themselves a CS rep). See docs/design-chat-and-
    // access.md §3 for the model. Until the first response arrives we
    // optimistically show My — it's the safe default and gets corrected
    // as soon as data loads.
    const visible_scopes = useMemo(() => {
        const out = [];
        if (!data || data.has_cs_exec !== false) out.push('my');
        if (data && data.is_cs_lead) out.push('team');
        if (data && data.is_admin) out.push('all');
        return out;
    }, [data]);

    // If the current scope drops out of the visible set (e.g., a rep without
    // a CS exec lands with scope='my' from a stale URL state, or an admin-
    // only user has My hidden), snap to the first visible tab so the page
    // doesn't get stuck on an invisible scope.
    useEffect(() => {
        if (visible_scopes.length === 0) return;
        if (!visible_scopes.includes(scope)) set_scope(visible_scopes[0]);
    }, [visible_scopes, scope]);

    // When the backend auto-promotes (admin without exec, no scope param →
    // scope='all'), reflect that in local state so the active-tab styling
    // is correct.
    useEffect(() => {
        if (data && data.auto_selected_scope && data.scope && data.scope !== scope) {
            set_scope(data.scope);
        }
    }, [data && data.auto_selected_scope, data && data.scope]); // eslint-disable-line react-hooks/exhaustive-deps

    const SCOPE_LABEL = { my: 'My', team: 'Team', all: 'All' };

    const ScopeTabs = () => (
        <Card style={{ marginBottom: 12 }}>
            <CardBody style={{ padding: 8 }}>
                <Nav pills style={{ gap: 4 }}>
                    {visible_scopes.map((v) => (
                        <NavItem key={v}>
                            <NavLink
                                href='#'
                                active={scope === v}
                                onClick={(e) => { e.preventDefault(); set_scope(v); }}
                            >
                                <b>{SCOPE_LABEL[v]}</b>
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            </CardBody>
        </Card>
    );

    if (loading && !data) {
        return (
            <div style={{ padding: '12px 16px' }}>
                <ScopeTabs />
                <div style={{ padding: 24, color: '#888' }}>Loading your portfolio…</div>
            </div>
        );
    }
    if (err) {
        return (
            <div style={{ padding: '12px 16px' }}>
                <ScopeTabs />
                <div style={{ padding: 24, color: '#c0392b' }}>Error: {err}</div>
            </div>
        );
    }
    if (!data || !data.clients) {
        return (
            <div style={{ padding: '12px 16px' }}>
                <ScopeTabs />
            </div>
        );
    }
    // Old rep-selector early-return removed: the backend now auto-promotes
    // admins-without-exec to scope='all', and the frontend tab gating hides
    // 'My' for them. If an admin wants to spot-check a specific rep, the
    // "Switch rep" inline banner below still exposes impersonation.
    if (data.not_a_cs_rep) {
        return (
            <div style={{ padding: 24 }}>
                <Card>
                    <CardBody style={{ textAlign: 'center', color: '#7f8c8d' }}>
                        <div style={{ fontSize: 16, marginBottom: 8 }}>This page is for CS reps.</div>
                        <div style={{ fontSize: 13 }}>
                            Your account isn't mapped to an active <code>CsExecutive</code> row, so there's no portfolio to show here. CS reps see findings + tasks across their owned clients on this page.
                        </div>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: '12px 16px' }}>
            <ScopeTabs />
            {/* Admin impersonation banner — visible whenever an admin is
                viewing someone else's portfolio. Clear visual cue to avoid
                "I thought I was on my own dashboard" confusion. */}
            {data.is_admin && data.rep ? (
                <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: '6px 12px', marginBottom: 8, borderRadius: 4, fontSize: 12 }}>
                    Admin view: showing <strong>{[data.rep.firstName, data.rep.lastName].filter(Boolean).join(' ') || data.rep.email}</strong>'s portfolio.{' '}
                    <a
                        href='#'
                        onClick={(e) => {
                            e.preventDefault();
                            set_impersonated_rep_email(null);
                        }}
                    >
                        Switch rep
                    </a>
                </div>
            ) : null}

            {/* Header summary: at-a-glance portfolio health */}
            <Card style={{ marginBottom: 12 }}>
                <CardBody style={{ padding: 12 }}>
                    <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase' }}>My book</div>
                            <div style={{ fontSize: 18, fontWeight: 600 }}>{data.totals.clients_with_work} clients with work</div>
                        </div>
                        {/* Stats reflect the active view: findings metrics on the
                            Findings/All view, task metrics on the Tasks/All view — so
                            the Tasks page doesn't headline finding counts. Finding
                            counts are active (OPEN) only (backend defaults to OPEN). */}
                        {/* Explicit units: 'clients with work' is a CLIENT count; the
                            severity/untriaged/task numbers count FINDINGS or TASKS. A
                            divider + unit-qualified labels keep the two from reading as
                            one ambiguous row. */}
                        {item_filter !== 'actionables' ? (
                            <>
                                <div style={{ alignSelf: 'stretch', borderLeft: '1px solid #ecf0f1' }} />
                                <Stat label='Findings · HIGH+' value={data.totals.findings_high_or_critical} color='#e67e22' />
                                <Stat label='Findings · untriaged' value={data.totals.findings_untriaged} color='#f1c40f' />
                            </>
                        ) : null}
                        {item_filter !== 'findings' ? (
                            <>
                                <div style={{ alignSelf: 'stretch', borderLeft: '1px solid #ecf0f1' }} />
                                <Stat label='Tasks · open' value={data.totals.actionables} color='#2f6fed' />
                                <Stat label='Tasks · due this week' value={data.totals.actionables_due_this_week} color='#c0392b' />
                            </>
                        ) : null}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                            <Button
                                size='sm'
                                color={chat_open ? 'secondary' : 'primary'}
                                outline={chat_open}
                                onClick={() => set_chat_open((v) => !v)}
                            >
                                {chat_open ? 'Close AI chat' : 'AI chat — portfolio'}
                            </Button>
                            <Button size='sm' onClick={load}>Refresh</Button>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* The rep's own promises from recent calls (cs-analysis meeting
                commitments): only on the Tasks page — a rep's own book in My
                scope, and for an admin in any scope (its rep picker is how an
                admin reaches a rep's offers). The card renders nothing when
                there is nothing to decide. Accepting one creates a task, so
                the list reloads. */}
            {show_meetings_card({ item_filter, scope, is_admin: data.is_admin }) ? (
                <MeetingCommitments rep_email={data.is_admin ? impersonated_rep_email : null} admin={!!data.is_admin} reps={reps} on_changed={load} />
            ) : null}

            {/* Filter chips + view mode toggle */}
            <Card style={{ marginBottom: 12 }}>
                <CardBody style={{ padding: 8, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    {kind ? null : (
                        <ChipGroup
                            label='Show'
                            value={item_filter}
                            onChange={set_item_filter}
                            options={[
                                { value: 'all', label: 'All' },
                                { value: 'findings', label: 'Findings' },
                                { value: 'actionables', label: 'Tasks' },
                            ]}
                        />
                    )}
                    <ChipGroup
                        label='Severity'
                        value={severity_filter}
                        onChange={set_severity_filter}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'HIGH+', label: 'HIGH+' },
                            { value: 'CRITICAL', label: 'CRITICAL' },
                            { value: 'HIGH', label: 'HIGH' },
                            { value: 'MEDIUM', label: 'MEDIUM' },
                        ]}
                    />
                    {/* The flat "By item" findings view is a table that sorts via
                        its own column headers, so the Severity/Newest bar would
                        conflict there — hide it. Keep it for the grouped "By
                        client" view and for the Tasks-only card view (no headers). */}
                    {(view_mode === 'by-client' || item_filter === 'actionables') ? (
                        <SortToggle
                            value={sort_by}
                            onChange={set_sort_by}
                            options={[
                                { value: 'severity', label: 'Severity' },
                                { value: 'newest', label: 'Newest' },
                            ]}
                        />
                    ) : null}
                    {/* CS-rep filter — only in Team/All scope, and only when the
                        portfolio actually spans >1 rep. */}
                    {rep_options.length > 1 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#7f8c8d' }}>Rep</span>
                            <Input
                                type='select'
                                bsSize='sm'
                                value={rep_filter}
                                onChange={(e) => set_rep_filter(e.target.value)}
                                style={{ width: 'auto', fontSize: 12 }}
                            >
                                <option value='all'>All reps</option>
                                {rep_options.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </Input>
                        </div>
                    ) : null}
                    {/* Active vs Archived (resolved/dismissed/done) findings + tasks. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase' }}>View</span>
                        <Button size='sm' outline={archived} color='primary' onClick={() => set_archived(false)}>Active</Button>
                        <Button size='sm' outline={!archived} color='secondary' onClick={() => set_archived(true)}>Archived</Button>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                        <Button
                            size='sm'
                            outline={view_mode !== 'by-client'}
                            color='primary'
                            onClick={() => set_view_mode('by-client')}
                            style={{ marginRight: 4 }}
                        >
                            By client
                        </Button>
                        <Button
                            size='sm'
                            outline={view_mode !== 'by-item'}
                            color='primary'
                            onClick={() => set_view_mode('by-item')}
                        >
                            By item
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* Body */}
            {visible_clients.length === 0 ? (
                <Card><CardBody style={{ textAlign: 'center', color: '#7f8c8d' }}>No work matches the current filters</CardBody></Card>
            ) : view_mode === 'by-client' ? (
                visible_clients.map((g) => (
                    <ClientCard
                        key={g.client.id}
                        group={g}
                        on_open_evidence={(ev) => set_evidence_open({ ev, storeId: g.client.storeId })}
                        on_open_history={(kind, item) => set_history_open({ kind, item, storeId: g.client.storeId })}
                        on_open_finding_modal={(finding) => open_finding_modal(finding, g.client.storeId)}
                        reps={reps}
                        rep_role={rep_role}
                        self_email={rep_self ? rep_self.email : ''}
                        busy_id={busy_id}
                        on_task_patch={patch_task}
                    />
                ))
            ) : (
                <FlatList
                    items={flat_items}
                    show_rep={scope !== 'my'}
                    on_open_evidence={(ev, storeId) => set_evidence_open({ ev, storeId })}
                    on_open_history={(kind, item, storeId) => set_history_open({ kind, item, storeId })}
                    on_open_finding_modal={(finding, storeId) => open_finding_modal(finding, storeId)}
                    reps={reps}
                    rep_role={rep_role}
                    self_email={rep_self ? rep_self.email : ''}
                    busy_id={busy_id}
                    on_task_patch={patch_task}
                />
            )}

            <EvidenceModal
                open={!!evidence_open.ev}
                on_close={() => set_evidence_open({ ev: null, storeId: null })}
                store_id={evidence_open.storeId}
                evidence={evidence_open.ev}
            />
            <ChatDrawer
                scope='portfolio'
                portfolio_scope={scope}
                open={chat_open}
                on_close={() => set_chat_open(false)}
            />
            <HistoryTimeline
                open={!!history_open.item}
                on_close={() => set_history_open({ kind: null, item: null, storeId: null })}
                store_id={history_open.storeId}
                kind={history_open.kind}
                item={history_open.item}
            />

            {/* Per-finding modal — reuses the same FindingDetailCard from the
                per-client view (chromeless=true to skip the position:fixed
                Card chrome, since reactstrap's Modal provides its own).
                Portfolio chat is closed when this opens (open_finding_modal
                handler), so the two surfaces never overlap on screen. */}
            <Modal
                isOpen={!!finding_modal.finding}
                toggle={close_finding_modal}
                size='xl'
                scrollable
            >
                <ModalBody style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                    {finding_modal.finding ? (
                        <FindingDetailCard
                            chromeless
                            selected={finding_modal.finding}
                            store_id={finding_modal.storeId}
                            busy={false}
                            update_finding={async () => { /* read-only from portfolio modal; mutations go through chat */ }}
                            set_selected={(v) => { if (v == null) close_finding_modal(); }}
                            set_rows={() => { /* portfolio data is refreshed on demand via load() */ }}
                            set_evidence_open={(ev) => set_evidence_open({ ev, storeId: finding_modal.storeId })}
                            refresh_one_finding={async () => { await load(); }}
                        />
                    ) : null}
                </ModalBody>
            </Modal>
        </div>
    );
};

// ---- Sub-components ----

const Stat = ({ label, value, color }) => (
    <div>
        <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: color || '#2c3e50' }}>{value}</div>
    </div>
);

const ChipGroup = ({ label, value, onChange, options }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase' }}>{label}</span>
        {options.map((o) => (
            <Button
                key={o.value}
                size='sm'
                outline={value !== o.value}
                color='primary'
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </Button>
        ))}
    </div>
);

const ClientCard = ({ group, on_open_evidence, on_open_history, on_open_finding_modal, reps, rep_role, self_email, busy_id, on_task_patch }) => {
    const [open, set_open] = useState(true);
    const c = group.client;
    return (
        <Card style={{ marginBottom: 10 }}>
            <CardBody style={{ padding: '8px 12px', background: '#fafbfc', borderBottom: '1px solid #ecf0f1', cursor: 'pointer' }} onClick={() => set_open((v) => !v)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 600 }}>
                        {open ? '▾ ' : '▸ '}{c.name}
                        <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 400, marginLeft: 8 }}>
                            {group.findings.length} finding(s), {group.actionables.length} task(s)
                        </span>
                    </div>
                    <a
                        href={`/home/store_dashboard/${encodeURIComponent(c.storeId)}`}
                        target='_blank'
                        rel='noreferrer noopener'
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 12 }}
                    >
                        Open client ↗
                    </a>
                </div>
            </CardBody>
            <Collapse isOpen={open}>
                <CardBody style={{ padding: '8px 12px' }}>
                    {group.findings.length > 0 ? (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Findings</div>
                            {group.findings.map((f) => (
                                <FindingRow
                                    key={f.id}
                                    finding={f}
                                    storeId={c.storeId}
                                    on_open_evidence={on_open_evidence}
                                    on_open_history={() => on_open_history('findings', f)}
                                    on_open_modal={() => on_open_finding_modal && on_open_finding_modal(f)}
                                />
                            ))}
                        </div>
                    ) : null}
                    {group.actionables.length > 0 ? (
                        <div>
                            <div style={{ fontSize: 10, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Tasks</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {group.actionables.map((a) => (
                                    <TaskCard
                                        key={a.id}
                                        row={a}
                                        store_id={c.storeId}
                                        busy={busy_id === a.id}
                                        on_patch={(id, body) => on_task_patch(c.storeId, id, body)}
                                        reps={reps}
                                        rep_role={rep_role}
                                        self_email={self_email}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                </CardBody>
            </Collapse>
        </Card>
    );
};

// Sortable cross-client findings table for the flat ("By item") view. Mirrors
// the per-client Findings tab: click any column header to sort (▲/▼), single
// active column. Adds a Client column since this view spans the whole book.
const PF_COLS = [
    { key: 'client', label: 'Client' },
    { key: 'severity', label: 'Sev' },
    { key: 'module', label: 'Module' },
    { key: 'title', label: 'Title' },
    { key: 'rep', label: 'Status' },
    { key: 'createdAt', label: 'Created' },
    { key: 'lastSeenAt', label: 'Last seen' },
];

const PORTFOLIO_PAGE_SIZE = 25;
const PortfolioFindingsTable = ({ rows, on_open_finding_modal, on_open_history, show_rep }) => {
    const [sort_col, set_sort_col] = useState('severity');
    const [sort_dir, set_sort_dir] = useState(1); // 1 asc, -1 desc
    const [page, set_page] = useState(0);
    const rep_name = (o) => (o ? ([o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.email || '—') : '—');
    const toggle = (key) => {
        if (key === sort_col) { set_sort_dir((d) => -d); return; }
        set_sort_col(key);
        set_sort_dir(key === 'createdAt' || key === 'lastSeenAt' ? -1 : 1);
    };
    const val = (r) => {
        const f = r.item;
        switch (sort_col) {
            case 'client': return (r.clientName || '').toLowerCase();
            case 'module': return (f.module || '').toLowerCase();
            case 'title': return (f.title || '').toLowerCase();
            case 'rep': return f.repTriagedAt ? 1 : 0;
            case 'createdAt': return f.createdAt ? new Date(f.createdAt).getTime() : 0;
            case 'lastSeenAt': return f.lastSeenAt ? new Date(f.lastSeenAt).getTime() : 0;
            default: return SEVERITY_ORDER[f.severity] != null ? SEVERITY_ORDER[f.severity] : 9;
        }
    };
    const sorted = rows.slice().sort((a, b) => {
        const av = val(a); const bv = val(b);
        if (av < bv) return -sort_dir;
        if (av > bv) return sort_dir;
        return 0;
    });
    const total = sorted.length;
    const page_count = Math.max(1, Math.ceil(total / PORTFOLIO_PAGE_SIZE));
    const cur = Math.min(page, page_count - 1);
    const paged = sorted.slice(cur * PORTFOLIO_PAGE_SIZE, (cur + 1) * PORTFOLIO_PAGE_SIZE);
    const th = { textAlign: 'left', padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 };
    const td = { padding: '6px 10px', color: '#7f8c8d', whiteSpace: 'nowrap' };
    return (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ fontSize: 11, color: '#95a5a6', marginBottom: 4 }}>click a column header to sort</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #ecf0f1' }}>
                        <th style={{ ...th, cursor: 'default', color: '#95a5a6', width: 36 }}>#</th>
                        {PF_COLS.map((c) => (
                            <th key={c.key} onClick={() => toggle(c.key)} style={{ ...th, color: sort_col === c.key ? '#2f6fed' : '#7f8c8d' }}>
                                {c.label}{sort_col === c.key ? (sort_dir === 1 ? ' ▲' : ' ▼') : ''}
                            </th>
                        ))}
                        {show_rep ? <th style={{ ...th, cursor: 'default' }}>Rep</th> : null}
                        <th style={{ ...th, cursor: 'default' }} />
                    </tr>
                </thead>
                <tbody>
                    {paged.map((r, i) => {
                        const f = r.item;
                        return (
                            <tr
                                key={f.id}
                                onClick={() => on_open_finding_modal && on_open_finding_modal(f, r.storeId)}
                                title='Open finding (chat + context + history)'
                                style={{ borderBottom: '1px solid #f0f2f5', cursor: 'pointer' }}
                            >
                                <td style={{ ...td, color: '#b2bec3', textAlign: 'right' }}>{cur * PORTFOLIO_PAGE_SIZE + i + 1}</td>
                                <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.clientName}>{r.clientName}</td>
                                <td style={{ padding: '6px 10px' }}><Badge style={{ backgroundColor: SEVERITY_COLOR[f.severity], color: '#fff' }}>{f.severity}</Badge></td>
                                <td style={td}>{f.module}</td>
                                <td style={{ padding: '6px 10px', fontSize: 13, color: '#2c3e50', whiteSpace: 'normal' }}>{f.title}</td>
                                <td style={{ padding: '6px 10px' }}>
                                    {f.repTriagedAt ? <Badge color='success' style={{ fontWeight: 400, fontSize: 10 }}>triaged</Badge>
                                        : f.status === 'OPEN' ? <Badge color='warning' style={{ fontWeight: 400, fontSize: 10 }}>needs review</Badge> : null}
                                </td>
                                <td style={td} title={f.createdAt ? fmt_datetime(f.createdAt) : ''}>{f.createdAt ? fmt_date(f.createdAt) : ''}</td>
                                <td style={td} title={f.lastSeenAt ? fmt_datetime(f.lastSeenAt) : ''}>{f.lastSeenAt ? fmt_date(f.lastSeenAt) : ''}</td>
                                {show_rep ? <td style={td} title={r.csOwner && r.csOwner.email ? r.csOwner.email : ''}>{rep_name(r.csOwner)}</td> : null}
                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                    {r.storeId ? (
                                        <a href={`/home/store_dashboard/${encodeURIComponent(r.storeId)}/findings`} target='_blank' rel='noreferrer noopener' onClick={(e) => e.stopPropagation()} style={{ fontSize: 11, padding: '0 4px' }} title='Open client → Findings'>↗</a>
                                    ) : null}
                                    <Button size='sm' outline color='link' onClick={(e) => { e.stopPropagation(); on_open_history && on_open_history('findings', f, r.storeId); }} style={{ padding: '0 4px', fontSize: 11 }} title='Audit history'>🕐</Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {page_count > 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: '#7f8c8d' }}>
                    <span>Showing {cur * PORTFOLIO_PAGE_SIZE + 1}–{Math.min((cur + 1) * PORTFOLIO_PAGE_SIZE, total)} of {total} findings</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                        <Button size='sm' outline disabled={cur <= 0} onClick={() => set_page(cur - 1)}>Prev</Button>
                        <span>Page {cur + 1} / {page_count}</span>
                        <Button size='sm' outline disabled={cur >= page_count - 1} onClick={() => set_page(cur + 1)}>Next</Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const FlatList = ({ items, on_open_history, on_open_finding_modal, reps, rep_role, self_email, busy_id, on_task_patch, show_rep }) => {
    const findings = items.filter((it) => it.type === 'finding');
    const tasks = items.filter((it) => it.type === 'actionable');
    const rep_name = (o) => (o ? ([o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.email || '') : '');
    const [task_page, set_task_page] = useState(0);
    const task_pages = Math.max(1, Math.ceil(tasks.length / PORTFOLIO_PAGE_SIZE));
    const task_cur = Math.min(task_page, task_pages - 1);
    const tasks_paged = tasks.slice(task_cur * PORTFOLIO_PAGE_SIZE, (task_cur + 1) * PORTFOLIO_PAGE_SIZE);
    return (
        <>
            {findings.length > 0 ? (
                <Card style={{ marginBottom: tasks.length ? 12 : 0 }}>
                    <CardBody style={{ padding: '8px 12px' }}>
                        <PortfolioFindingsTable
                            rows={findings}
                            on_open_finding_modal={on_open_finding_modal}
                            on_open_history={on_open_history}
                            show_rep={show_rep}
                        />
                    </CardBody>
                </Card>
            ) : null}
            {tasks.length > 0 ? (
                <Card>
                    <CardBody style={{ padding: '8px 12px' }}>
                        {tasks_paged.map((it, i) => (
                            // Tasks render as the full-width shared card; serial# + client (+ rep) above it.
                            <div key={`${it.type}:${it.item.id}`} style={{ padding: '8px 0', borderBottom: '1px solid #ecf0f1' }}>
                                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 4 }}>
                                    <span style={{ color: '#b2bec3' }}>{task_cur * PORTFOLIO_PAGE_SIZE + i + 1}. </span>
                                    {it.clientName}
                                    {show_rep && it.csOwner ? <span style={{ color: '#95a5a6' }}> · rep: {rep_name(it.csOwner)}</span> : null}
                                </div>
                                <TaskCard
                                    row={it.item}
                                    store_id={it.storeId}
                                    busy={busy_id === it.item.id}
                                    on_patch={(id, body) => on_task_patch(it.storeId, id, body)}
                                    reps={reps}
                                    rep_role={rep_role}
                                    self_email={self_email}
                                />
                            </div>
                        ))}
                        {task_pages > 1 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: '#7f8c8d' }}>
                                <span>Showing {task_cur * PORTFOLIO_PAGE_SIZE + 1}–{Math.min((task_cur + 1) * PORTFOLIO_PAGE_SIZE, tasks.length)} of {tasks.length} tasks</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <Button size='sm' outline disabled={task_cur <= 0} onClick={() => set_task_page(task_cur - 1)}>Prev</Button>
                                    <span>Page {task_cur + 1} / {task_pages}</span>
                                    <Button size='sm' outline disabled={task_cur >= task_pages - 1} onClick={() => set_task_page(task_cur + 1)}>Next</Button>
                                </div>
                            </div>
                        ) : null}
                    </CardBody>
                </Card>
            ) : null}
        </>
    );
};

/**
 * Portfolio-scan row. Read-only: click anywhere on the row top to expand
 * inline context (observation + evidence chips); the Audit-history (🕐)
 * button opens the frozen ledger; the "Open client →" link opens the per-
 * client Client Health tab in a new window where the new chat-based
 * workflow lives (status changes, notes, ProposedSolution conversion).
 *
 * No inline Accept/Resolve/Dismiss + no rep-note textarea — those were the
 * old workflow; status + notes are now driven through the per-finding chat
 * in the drawer.
 */
const FindingRow = ({ finding, storeId, on_open_evidence, on_open_history, on_open_modal }) => {
    return (
        <div style={{ marginBottom: 6 }}>
            <div
                style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer', padding: '4px 0' }}
                onClick={() => on_open_modal && on_open_modal()}
                title='Open finding (chat + context + history)'
            >
                <Badge style={{ backgroundColor: SEVERITY_COLOR[finding.severity], color: '#fff', flexShrink: 0 }}>{finding.severity}</Badge>
                <span style={{ fontSize: 12, color: '#7f8c8d', flexShrink: 0 }}>{finding.module}</span>
                <span style={{ flex: 1, fontSize: 13 }}>{finding.title}</span>
                {finding.repTriagedAt ? (
                    <Badge color='success' style={{ fontWeight: 400, fontSize: 10 }}>triaged</Badge>
                ) : finding.status === 'OPEN' ? (
                    <Badge color='warning' style={{ fontWeight: 400, fontSize: 10 }}>needs review</Badge>
                ) : null}
                {finding.createdAt ? (
                    <span
                        style={{ fontSize: 11, color: '#7f8c8d', flexShrink: 0 }}
                        title={`Created ${fmt_datetime(finding.createdAt)}`}
                    >
                        {fmt_date(finding.createdAt)}
                    </span>
                ) : null}
                {storeId ? (
                    <a
                        href={`/home/store_dashboard/${encodeURIComponent(storeId)}/findings`}
                        target='_blank'
                        rel='noreferrer noopener'
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 11, padding: '0 4px' }}
                        title='Open client → Findings tab in new window'
                    >
                        ↗
                    </a>
                ) : null}
                <Button
                    size='sm' outline color='link'
                    onClick={(e) => { e.stopPropagation(); on_open_history && on_open_history(); }}
                    style={{ padding: '0 4px', fontSize: 11 }}
                    title='Audit history'
                >
                    🕐
                </Button>
            </div>
        </div>
    );
};

// ---- helpers ----

// mutate_group helper removed — only patch_finding / patch_actionable
// used it, and those are gone with the inline mutation row removal.
// Due-date coloring now uses the shared `due_color` from utils/task_due.

function filter_findings(findings, item_filter, severity_filter) {
    if (item_filter === 'actionables') return [];
    return findings.filter((f) => {
        if (severity_filter === 'all') return true;
        if (severity_filter === 'HIGH+') return f.severity === 'CRITICAL' || f.severity === 'HIGH';
        return f.severity === severity_filter;
    });
}

function filter_actionables(actionables, item_filter) {
    if (item_filter === 'findings') return [];
    return actionables;
}

export default MyWork;
